import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { CreatePurchaseOrderInput, CreatePurchaseOrderItemInput } from '@/types/domain/po-management';
import { MOCK_VENDORS, MOCK_PURCHASE_REQUESTS } from '@/mocks/database';
import { calculateLineItemTotal, calculateOrderTotals } from '@/features/purchase-orders/utils/poCalculations';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { formatCurrency } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';
import {
  ShoppingBag,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Building2,
  Package,
  Truck,
  Plus,
  Trash2,
  ShieldCheck,
  AlertCircle,
  FileText,
} from 'lucide-react';

import { SupplierSelectionPayload } from '@/types/domain/quotation-comparison';

export const PurchaseOrderCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Location state from quotation comparison or pipeline handoff
  const locationState = location.state as {
    supplierSelectionPayload?: SupplierSelectionPayload;
    prPayload?: { prId: string };
  } | null;

  const handoffPayload = locationState?.supplierSelectionPayload;
  const prHandoffPayload = locationState?.prPayload;

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<CreatePurchaseOrderInput>({
    prId: prHandoffPayload?.prId || 'pr-1001',
    rfqId: handoffPayload?.rfqId || 'rfq-2001',
    quotationId: handoffPayload?.selectedQuotationId || 'quo-3001',
    vendorId: handoffPayload?.selectedVendorId || MOCK_VENDORS[0]?.id || 'vend-1',
    vendorName: handoffPayload?.selectedVendorName || MOCK_VENDORS[0]?.name || 'Apex Hardware Solutions',
    vendorCode: 'VND-001',
    companyCode: '1000',
    plant: 'PLANT-1000',
    purchasingOrganization: '1000',
    purchasingGroup: 'PGRP-001',
    buyer: 'David Sterling',
    currency: handoffPayload?.currency || 'USD',
    requiredDate: '2026-11-15',
    promisedDeliveryDate: '2026-11-15',
    paymentTerms: 'Net 30',
    incoterms: 'DDP',
    deliveryTerms: 'Standard Ground Freight',
    shippingAddress: 'Plant 1000 - Receiving Dock 4, 100 Enterprise Way, Austin, TX 78701',
    billingAddress: 'Corporate Accounts Payable, ProcureAI Global HQ, San Jose, CA 95110',
    notes: handoffPayload?.justificationReason
      ? `Awarded based on quotation comparison: ${handoffPayload.justificationReason}`
      : 'Standard commercial Purchase Order issued under Master Supply Agreement.',
    discount: 0,
    freight: 0,
    items: [
      {
        materialCode: 'MAT-IT-101',
        description: 'ThinkPad Enterprise Mobile Workstation',
        specification: 'Intel Core i9, 32GB RAM, 1TB NVMe SSD',
        quantityOrdered: 5,
        uom: 'EA',
        unitPrice: 2200,
        discountAmount: 0,
        taxPercent: 18,
        deliveryDate: '2026-11-15',
        plant: 'PLANT-1000',
        storageLocation: 'SLOC-01',
        costCenter: 'CC-IT-101',
        glAccount: '500100',
        accountAssignment: 'K',
      },
    ],
  });

  useEffect(() => {
    if (handoffPayload) {
      setFormData((prev) => ({
        ...prev,
        rfqId: handoffPayload.rfqId,
        quotationId: handoffPayload.selectedQuotationId,
        vendorId: handoffPayload.selectedVendorId,
        vendorName: handoffPayload.selectedVendorName,
        currency: handoffPayload.currency || prev.currency,
        notes: `Selected from RFQ ${handoffPayload.rfqNumber || handoffPayload.rfqId}. Justification: ${handoffPayload.justificationReason || 'Lowest evaluated total commercial quote.'}`,
      }));
    }
  }, [handoffPayload]);

  const handleVendorChange = (vendorId: string) => {
    const selected = MOCK_VENDORS.find((v) => v.id === vendorId);
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        vendorId: selected.id,
        vendorName: selected.name,
        vendorCode: selected.vendorCode || `VND-${selected.id.toUpperCase()}`,
      }));
    }
  };

  const handleAddItem = () => {
    const newItem: CreatePurchaseOrderItemInput = {
      materialCode: `MAT-${Math.floor(100 + Math.random() * 900)}`,
      description: 'Standard Procurement Line Item',
      specification: 'Standard Spec',
      quantityOrdered: 1,
      uom: 'EA',
      unitPrice: 500,
      discountAmount: 0,
      taxPercent: 18,
      deliveryDate: formData.promisedDeliveryDate,
      plant: formData.plant,
      storageLocation: 'SLOC-01',
      costCenter: 'CC-IT-101',
      glAccount: '500100',
      accountAssignment: 'K',
    };
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const handleRemoveItem = (index: number) => {
    if (formData.items.length <= 1) {
      alert('Purchase Order must contain at least one line item.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = <K extends keyof CreatePurchaseOrderItemInput>(
    index: number,
    field: K,
    value: CreatePurchaseOrderItemInput[K]
  ) => {
    setFormData((prev) => {
      const updated = [...prev.items];
      const target = { ...updated[index]!, [field]: value };
      updated[index] = target;
      return { ...prev, items: updated };
    });
  };

  const totals = calculateOrderTotals(formData.items, formData.discount || 0, formData.freight || 0);

  const handleSubmit = async (submitForApproval: boolean) => {
    if (formData.items.length === 0) {
      setErrorMessage('Please provide at least one valid line item.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const idempotencyKey = `po-create-${Date.now()}-${Math.random()}`;
      const created = await mockPurchaseOrderService.createPurchaseOrder(
        { ...formData, submitForApproval },
        idempotencyKey
      );
      navigate(ROUTES.PURCHASE_ORDERS.DETAILS(created.id));
    } catch (err: unknown) {
      setErrorMessage((err as Error).message || 'Failed to create Purchase Order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, label: 'Source Context', icon: FileText },
    { id: 2, label: 'Supplier & Org', icon: Building2 },
    { id: 3, label: 'Line Items', icon: Package },
    { id: 4, label: 'Commercial & Shipping', icon: Truck },
    { id: 5, label: 'Review & Submit', icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6 pb-16 font-heading max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.LIST)}
            className="text-xs font-bold text-foreground-secondary hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Purchase Orders
          </button>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <ShoppingBag className="h-7 w-7 text-primary" /> Create Purchase Order
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1">
            Generate an authorized commercial commitment linked to approved procurement requests and quotation awards.
          </p>
        </div>

        {handoffPayload && (
          <div className="px-3.5 py-1.5 rounded-xl border border-purple-500/20 bg-purple-500/10 text-purple-700 text-xs font-bold flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> Traceable to Quotation Comparison ({handoffPayload.rfqId})
          </div>
        )}
      </div>

      {/* Wizard Progress Steps */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-2 rounded-2xl border border-border bg-card shadow-2xs">
        {steps.map((step) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isDone = currentStep > step.id;

          return (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className={`flex items-center gap-2.5 p-2.5 rounded-xl transition-all text-left ${
                isActive
                  ? 'bg-primary/10 text-primary border border-primary/30 font-extrabold'
                  : isDone
                  ? 'text-emerald-600 font-bold'
                  : 'text-foreground-secondary font-medium hover:bg-surface'
              }`}
            >
              <div
                className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs ${
                  isActive
                    ? 'bg-primary text-white'
                    : isDone
                    ? 'bg-emerald-500/20 text-emerald-700'
                    : 'bg-surface text-foreground-muted'
                }`}
              >
                {isDone ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span className="text-xs whitespace-nowrap">{step.label}</span>
            </button>
          );
        })}
      </div>

      {errorMessage && (
        <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-700 text-xs font-bold flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {errorMessage}
        </div>
      )}

      {/* Wizard Step 1: Procurement Source Selection */}
      {currentStep === 1 && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-4">
              <h2 className="text-base font-extrabold text-foreground">Step 1 — Upstream Procurement Traceability</h2>
              <p className="text-xs text-foreground-secondary">
                Bind this Purchase Order to approved Requisitions, RFQs, or Quotations to ensure enterprise audit compliance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Source Purchase Request (PR)</label>
                <select
                  value={formData.prId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, prId: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground focus:ring-1 focus:ring-primary"
                >
                  {MOCK_PURCHASE_REQUESTS.slice(0, 15).map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.requestNumber} — {pr.title} ({formatCurrency(pr.totalAmount, 'USD')})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Source RFQ Identifier</label>
                <input
                  type="text"
                  value={formData.rfqId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, rfqId: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground focus:ring-1 focus:ring-primary"
                  placeholder="e.g. rfq-2001"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Selected Quotation Reference</label>
                <input
                  type="text"
                  value={formData.quotationId}
                  onChange={(e) => setFormData((prev) => ({ ...prev, quotationId: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground focus:ring-1 focus:ring-primary"
                  placeholder="e.g. quo-3001"
                />
              </div>
            </div>

            <div className="p-4 rounded-xl bg-surface border border-border/70 text-xs text-foreground-secondary space-y-1">
              <span className="font-extrabold text-foreground block">Enterprise Governance Notice:</span>
              Traceability links will be embedded in the SAP BAPI payload header and permanently stamped into the SOX compliance audit log.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wizard Step 2: Supplier & Purchasing Org Data */}
      {currentStep === 2 && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-4">
              <h2 className="text-base font-extrabold text-foreground">Step 2 — Supplier & Organizational Assignment</h2>
              <p className="text-xs text-foreground-secondary">
                Assign authorized vendor master data, purchasing organization, company code, and plant.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Selected Vendor Master</label>
                <select
                  value={formData.vendorId}
                  onChange={(e) => handleVendorChange(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground focus:ring-1 focus:ring-primary"
                >
                  {MOCK_VENDORS.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.category} — {v.vendorCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Procurement Officer / Buyer</label>
                <input
                  type="text"
                  value={formData.buyer}
                  onChange={(e) => setFormData((prev) => ({ ...prev, buyer: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Company Code</label>
                <input
                  type="text"
                  value={formData.companyCode}
                  onChange={(e) => setFormData((prev) => ({ ...prev, companyCode: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Plant & Storage Site</label>
                <input
                  type="text"
                  value={formData.plant}
                  onChange={(e) => setFormData((prev) => ({ ...prev, plant: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Purchasing Organization</label>
                <input
                  type="text"
                  value={formData.purchasingOrganization}
                  onChange={(e) => setFormData((prev) => ({ ...prev, purchasingOrganization: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Purchasing Group</label>
                <input
                  type="text"
                  value={formData.purchasingGroup}
                  onChange={(e) => setFormData((prev) => ({ ...prev, purchasingGroup: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wizard Step 3: Line Items */}
      {currentStep === 3 && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
              <div>
                <h2 className="text-base font-extrabold text-foreground">Step 3 — Purchase Order Line Items</h2>
                <p className="text-xs text-foreground-secondary">
                  Specify materials, quantities, contractual unit prices, tax percentage, and cost center accounting.
                </p>
              </div>
              <Button size="sm" onClick={handleAddItem} className="rounded-xl font-bold bg-primary text-white">
                <Plus className="h-4 w-4 mr-1.5" /> Add Line Item
              </Button>
            </div>

            <div className="space-y-4">
              {formData.items.map((item, idx) => {
                const itemCalc = calculateLineItemTotal(item.quantityOrdered, item.unitPrice, item.discountAmount, item.taxPercent);

                return (
                  <div key={idx} className="p-4 rounded-xl border border-border bg-card shadow-2xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-extrabold text-primary">Line {(idx + 1) * 10}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveItem(idx)}
                        className="text-rose-600 hover:text-rose-700 h-7 px-2"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="md:col-span-2 space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Description / Item Name</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Material Code</label>
                        <input
                          type="text"
                          value={item.materialCode}
                          onChange={(e) => handleItemChange(idx, 'materialCode', e.target.value)}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium font-mono"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Cost Center</label>
                        <input
                          type="text"
                          value={item.costCenter}
                          onChange={(e) => handleItemChange(idx, 'costCenter', e.target.value)}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantityOrdered}
                          onChange={(e) => handleItemChange(idx, 'quantityOrdered', Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium font-mono"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Unit of Measure (UOM)</label>
                        <select
                          value={item.uom}
                          onChange={(e) => handleItemChange(idx, 'uom', e.target.value)}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium"
                        >
                          <option value="EA">EA - Each</option>
                          <option value="SET">SET - Kit / Set</option>
                          <option value="KG">KG - Kilogram</option>
                          <option value="M">M - Meter</option>
                          <option value="HR">HR - Service Hours</option>
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Unit Price ({formData.currency})</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(idx, 'unitPrice', Math.max(0, parseFloat(e.target.value) || 0))}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium font-mono"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-foreground">Tax Rate (%)</label>
                        <select
                          value={item.taxPercent}
                          onChange={(e) => handleItemChange(idx, 'taxPercent', parseFloat(e.target.value) || 0)}
                          className="w-full h-9 px-3 rounded-lg border border-border bg-surface text-xs font-medium"
                        >
                          <option value="0">0% (Exempt)</option>
                          <option value="5">5% (Standard GST)</option>
                          <option value="12">12% (Intermediate)</option>
                          <option value="18">18% (Standard Enterprise)</option>
                          <option value="28">28% (Luxury / Special)</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs">
                      <span className="text-foreground-secondary">
                        Line Calculation: Subtotal {formatCurrency(itemCalc.lineSubtotal, formData.currency)} + Tax ({item.taxPercent}%) {formatCurrency(itemCalc.taxAmount, formData.currency)}
                      </span>
                      <span className="font-extrabold text-foreground">
                        Line Total: {formatCurrency(itemCalc.lineTotal, formData.currency)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wizard Step 4: Commercial & Shipping */}
      {currentStep === 4 && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-4">
              <h2 className="text-base font-extrabold text-foreground">Step 4 — Commercial Terms & Shipping Specifications</h2>
              <p className="text-xs text-foreground-secondary">
                Set payment milestones, Incoterms, promised delivery target, and delivery address.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Payment Terms</label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData((prev) => ({ ...prev, paymentTerms: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                >
                  <option value="Net 30">Net 30 Days</option>
                  <option value="Net 60">Net 60 Days</option>
                  <option value="Net 90">Net 90 Days</option>
                  <option value="2% 10 Net 30">2% 10 Net 30 (Cash Discount)</option>
                  <option value="100% Advance">100% Advance Payment</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Incoterms</label>
                <select
                  value={formData.incoterms}
                  onChange={(e) => setFormData((prev) => ({ ...prev, incoterms: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                >
                  <option value="DDP">DDP - Delivered Duty Paid</option>
                  <option value="FOB">FOB - Free on Board</option>
                  <option value="CIF">CIF - Cost, Insurance and Freight</option>
                  <option value="EXW">EXW - Ex Works</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Promised Delivery Date</label>
                <input
                  type="date"
                  value={formData.promisedDeliveryDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, promisedDeliveryDate: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-semibold text-foreground"
                />
              </div>

              <div className="md:col-span-3 space-y-1.5">
                <label className="text-xs font-bold text-foreground">Destination Delivery Address</label>
                <input
                  type="text"
                  value={formData.shippingAddress}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shippingAddress: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-medium"
                />
              </div>

              <div className="md:col-span-3 space-y-1.5">
                <label className="text-xs font-bold text-foreground">Billing / Accounts Payable Address</label>
                <input
                  type="text"
                  value={formData.billingAddress}
                  onChange={(e) => setFormData((prev) => ({ ...prev, billingAddress: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-xs font-medium"
                />
              </div>

              <div className="md:col-span-3 space-y-1.5">
                <label className="text-xs font-bold text-foreground">Procurement Instructions & Delivery Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full p-3 rounded-xl border border-border bg-surface text-xs font-medium resize-none"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wizard Step 5: Review & Submit */}
      {currentStep === 5 && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-4">
              <h2 className="text-base font-extrabold text-foreground">Step 5 — Final Financial Verification & Submission</h2>
              <p className="text-xs text-foreground-secondary">
                Verify calculations, commercial commitment accuracy, and approval workflow readiness.
              </p>
            </div>

            {/* Summary Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border border-border bg-surface/60">
                <span className="text-[10px] uppercase font-bold text-foreground-secondary block">Selected Supplier</span>
                <span className="text-sm font-extrabold text-foreground">{formData.vendorName}</span>
                <span className="text-xs text-foreground-muted block mt-0.5">{formData.vendorCode}</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface/60">
                <span className="text-[10px] uppercase font-bold text-foreground-secondary block">Delivery Target</span>
                <span className="text-sm font-extrabold text-foreground">{formData.promisedDeliveryDate}</span>
                <span className="text-xs text-foreground-muted block mt-0.5">{formData.plant}</span>
              </div>

              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5">
                <span className="text-[10px] uppercase font-bold text-primary block">Total Commercial Value</span>
                <span className="text-lg font-black text-primary">{formatCurrency(totals.totalValue, formData.currency)}</span>
                <span className="text-xs text-foreground-secondary block mt-0.5">{totals.itemCount} items ({totals.totalOrderedUnits} units)</span>
              </div>
            </div>

            {/* Calculation Breakdown */}
            <div className="p-4 rounded-xl border border-border bg-card space-y-2 text-xs">
              <div className="flex justify-between text-foreground-secondary">
                <span>Line Items Subtotal:</span>
                <span className="font-mono font-bold text-foreground">{formatCurrency(totals.subtotal, formData.currency)}</span>
              </div>
              <div className="flex justify-between text-foreground-secondary">
                <span>Taxes (Aggregated Line Taxes):</span>
                <span className="font-mono font-bold text-foreground">{formatCurrency(totals.tax, formData.currency)}</span>
              </div>
              <div className="flex justify-between text-foreground-secondary">
                <span>Freight & Handling:</span>
                <span className="font-mono font-bold text-foreground">{formatCurrency(totals.freight, formData.currency)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border text-sm font-extrabold text-foreground">
                <span>Grand Total Commitment:</span>
                <span className="font-mono text-primary font-black">{formatCurrency(totals.totalValue, formData.currency)}</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-xs text-emerald-800 space-y-1">
              <div className="flex items-center gap-1.5 font-extrabold">
                <CheckCircle className="h-4 w-4 text-emerald-600" /> Internal Integrity & Compliance Verified
              </div>
              <p className="text-[11px] text-emerald-700">
                All mandatory organizational assignments, budget cost centers, and line item calculations are complete.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-border/60">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
          disabled={currentStep === 1 || isSubmitting}
          className="rounded-xl font-bold border-border"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Previous Step
        </Button>

        <div className="flex items-center gap-3">
          {currentStep < 5 ? (
            <Button
              size="sm"
              onClick={() => setCurrentStep((prev) => Math.min(5, prev + 1))}
              className="rounded-xl font-bold bg-primary text-white"
            >
              Next Step <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting}
                className="rounded-xl font-bold border-border"
              >
                Save as Draft
              </Button>
              <Button
                size="sm"
                onClick={() => handleSubmit(true)}
                disabled={isSubmitting}
                className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs"
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Authorization'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
